import { useState } from "react"
import {
  IconCopy,
  IconTrash,
  IconPlus,
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconKey,
  IconDots,
} from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { DataTable, type DataTableColumn } from "@/components/ui/data-table"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
} from "@/components/Modal"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { toast } from "sonner"
import { ApiError } from "@/data/api/client"
import { formatDate, formatDateTime } from "@/lib/utils"
import type { ApiKeyListItem } from "@/data/types/apiKeys.types"
import { PageContainer } from "@/components/PageContainer"
import { PageHeader } from "@/components/PageHeader"
import { Chip } from "@/components/Chip"
import {
  useApiKeys,
  useCreateApiKey,
  useUpdateApiKey,
  useActivateApiKey,
  useDeactivateApiKey,
  useDeleteApiKey,
} from "@/data/hooks/api"

export function ApiKeysPage() {
  // React Query hooks
  const { data: apiKeysData, isLoading: loading } = useApiKeys()
  const apiKeys = apiKeysData?.items ?? []
  const createApiKeyMutation = useCreateApiKey()
  const updateApiKeyMutation = useUpdateApiKey()
  const activateApiKeyMutation = useActivateApiKey()
  const deactivateApiKeyMutation = useDeactivateApiKey()
  const deleteApiKeyMutation = useDeleteApiKey()

  // Create dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newKeyName, setNewKeyName] = useState("")
  const [newlyCreatedKeyData, setNewlyCreatedKeyData] = useState<{
    name: string
    key: string
  } | null>(null)

  // Edit dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingKey, setEditingKey] = useState<ApiKeyListItem | null>(null)
  const [editKeyName, setEditKeyName] = useState("")

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deletingKey, setDeletingKey] = useState<ApiKeyListItem | null>(null)

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("API key name is required")
      return
    }

    try {
      const newKey = await createApiKeyMutation.mutateAsync({ name: newKeyName })
      // Store the full key for display
      setNewlyCreatedKeyData({
        name: newKey.name,
        key: newKey.key,
      })
      setNewKeyName("")
      toast.success("API key created successfully")
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message)
      } else {
        toast.error("Failed to create API key")
      }
    }
  }

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false)
    setNewKeyName("")
    setNewlyCreatedKeyData(null)
  }

  const handleOpenEditDialog = (key: ApiKeyListItem) => {
    setEditingKey(key)
    setEditKeyName(key.name)
    setIsEditDialogOpen(true)
  }

  const handleUpdateApiKey = async () => {
    if (!editingKey) return

    if (!editKeyName.trim()) {
      toast.error("API key name is required")
      return
    }

    try {
      await updateApiKeyMutation.mutateAsync({
        apiKeyId: editingKey.id,
        data: { name: editKeyName },
      })
      setIsEditDialogOpen(false)
      setEditingKey(null)
      setEditKeyName("")
      toast.success("API key updated successfully")
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message)
      } else {
        toast.error("Failed to update API key")
      }
    }
  }

  const handleToggleActiveStatus = async (key: ApiKeyListItem) => {
    try {
      if (key.is_active) {
        await deactivateApiKeyMutation.mutateAsync(key.id)
        toast.success("API key deactivated successfully")
      } else {
        await activateApiKeyMutation.mutateAsync(key.id)
        toast.success("API key activated successfully")
      }
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message)
      } else {
        toast.error("Failed to update API key status")
      }
    }
  }

  const handleOpenDeleteDialog = (key: ApiKeyListItem) => {
    setDeletingKey(key)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteApiKey = async () => {
    if (!deletingKey) return

    try {
      await deleteApiKeyMutation.mutateAsync(deletingKey.id)
      setIsDeleteDialogOpen(false)
      setDeletingKey(null)
      toast.success("API key deleted successfully")
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message)
      } else {
        toast.error("Failed to delete API key")
      }
    }
  }

  const maskKey = (keyPrefix: string) => {
    // keyPrefix is like "ksk_JNFu1YJZ"
    // Display as "ksk_JNFu1YJZ••••••••"
    return keyPrefix + "••••••••"
  }

  const copyToClipboard = (text: string, label: string = "API key") => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`)
    })
  }


  // Column definitions for the API keys table
  const apiKeysTableColumns: DataTableColumn<ApiKeyListItem>[] = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      flex: 1.3,
      enableResizing: true,
      cell: ({ row }) => (
        <span className="font-normal text-[#08090A]">{row.original.name}</span>
      ),
    },
    {
      id: "key_prefix",
      header: "API Key",
      accessorKey: "key_prefix",
      flex: 1.1,
      enableSorting: false,
      cell: ({ row }) => (
        <code className="rounded bg-[#F7F8F8] border border-[#EDEEF3] px-[6px] py-[2px] text-[11px] font-mono text-[#08090A]">
          {maskKey(row.original.key_prefix)}
        </code>
      ),
    },
    {
      id: "created_at",
      header: "Created",
      accessorKey: "created_at",
      flex: 0.8,
      cell: ({ row }) => <span className="text-[#8A8F98]">{formatDate(row.original.created_at)}</span>,
    },
    {
      id: "last_used_at",
      header: "Last Used",
      accessorKey: "last_used_at",
      flex: 0.7,
      cell: ({ row }) => (
        <span className="text-[#8A8F98]">
          {row.original.last_used_at ? formatDateTime(row.original.last_used_at) : "Never"}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      flex: 0.5,
      cell: ({ row }) => {
        const isActive = row.original.is_active
        return (
          <Chip
            label={isActive ? "Active" : "Inactive"}
            showDot
            dotColor={isActive ? "#2ECC71" : "#34495E"}
          />
        )
      },
    },
    {
      id: "actions",
      header: "",
      flex: 0.4,
      enableSorting: false,
      cell: ({ row }) => {
        const apiKey = row.original
        return (
          <div className="flex items-center justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center justify-center size-[28px] rounded-[4px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3] transition-colors cursor-pointer"
                >
                  <IconDots className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px] p-[4px] rounded-[6px] border-[#EDEEF3] shadow-lg">
                <DropdownMenuItem
                  onClick={() => handleOpenEditDialog(apiKey)}
                  className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                >
                  <IconPencil className="mr-[8px] size-3.5 text-[#8A8F98]" />
                  Edit Name
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleToggleActiveStatus(apiKey)}
                  className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                >
                  {apiKey.is_active ? (
                    <>
                      <IconPlayerPause className="mr-[8px] size-3.5 text-[#8A8F98]" />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <IconPlayerPlay className="mr-[8px] size-3.5 text-[#8A8F98]" />
                      Activate
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-[4px] bg-[#EDEEF3]" />
                <DropdownMenuItem
                  onClick={() => handleOpenDeleteDialog(apiKey)}
                  className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#E74C3C] cursor-pointer focus:text-[#E74C3C] focus:bg-[#E74C3C]/5"
                >
                  <IconTrash className="mr-[8px] size-3.5" />
                  Delete Key
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <PageContainer>
        <PageHeader
          title="API Keys"
          description="Manage your API keys for programmatic access to your account."
        />

        <div className="space-y-[13px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">All API Keys</h2>
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium px-[13px] rounded-[6px]"
            >
              <IconPlus className="size-4" />
              Create New Key
            </Button>
          </div>
          <DataTable
            data={apiKeys}
            columns={apiKeysTableColumns}
            loading={loading}
            getRowId={(row) => row.id}
            emptyMessage="No API keys found"
            emptyIcon={<IconKey className="size-6 text-[#8A8F98]" />}
            pagination={{
              enabled: true,
              mode: "client",
              defaultPageSize: 10,
              pageSizeOptions: [5, 10, 20],
            }}
          />
        </div>
      </PageContainer>

      {/* Create API Key Modal */}
      <Modal open={isCreateDialogOpen} onOpenChange={handleCloseCreateDialog}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">
              {newlyCreatedKeyData ? "API Key Created" : "Create New API Key"}
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              {newlyCreatedKeyData
                ? "Save this API key now. You won't be able to see it again!"
                : "Enter a name for your new API key to help you identify it later."}
            </ModalDescription>
          </ModalHeader>

          {newlyCreatedKeyData ? (
            <div className="px-[24px] py-[24px] space-y-[20px]">
              <div className="space-y-[8px]">
                <Label className="text-[13px] text-[#08090A]">Name</Label>
                <div className="h-[36px] px-[13px] rounded-[6px] border border-[#EDEEF3] bg-[#F7F8F8] flex items-center text-[13px] text-[#08090A]">
                  {newlyCreatedKeyData.name}
                </div>
              </div>
              <div className="space-y-[8px]">
                <Label className="text-[13px] text-[#08090A]">API Key</Label>
                <div className="flex items-center gap-[8px]">
                  <code className="flex-1 h-[36px] px-[13px] rounded-[6px] border border-[#EDEEF3] bg-[#F7F8F8] flex items-center text-[13px] text-[#08090A] overflow-hidden truncate font-mono">
                    {newlyCreatedKeyData.key}
                  </code>
                  <Button
                    variant="transparent-outline"
                    size="icon"
                    className="size-[36px] border-[#EDEEF3]"
                    onClick={() => copyToClipboard(newlyCreatedKeyData.key)}
                  >
                    <IconCopy className="size-4" />
                  </Button>
                </div>
                <p className="text-[12px] text-[#E67E22] font-medium">
                  ⚠️ Make sure to copy your API key now. You won't be able to see it again!
                </p>
              </div>
            </div>
          ) : (
            <div className="px-[24px] py-[24px] space-y-[20px]">
              <div className="space-y-[8px]">
                <Label htmlFor="key-name" className="text-[13px] text-[#08090A]">Key Name *</Label>
                <Input
                  id="key-name"
                  placeholder="e.g., Production API Key"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  disabled={createApiKeyMutation.isPending}
                  className="h-[36px] px-[13px] rounded-[6px] border-[#EDEEF3] bg-[#FFFFFF] text-[13px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus-visible:ring-0 focus-visible:border-[#654EDA] transition-colors shadow-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !createApiKeyMutation.isPending) {
                      handleCreateApiKey()
                    }
                  }}
                />
              </div>
            </div>
          )}

          <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
            {newlyCreatedKeyData ? (
              <Button
                onClick={handleCloseCreateDialog}
                className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
                size="sm"
              >
                Done
              </Button>
            ) : (
              <>
                <Button
                  variant="transparent-outline"
                  size="sm"
                  onClick={handleCloseCreateDialog}
                  disabled={createApiKeyMutation.isPending}
                  className="h-[32px] font-medium border-[#EDEEF3]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateApiKey}
                  disabled={createApiKeyMutation.isPending}
                  size="sm"
                  className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
                >
                  {createApiKeyMutation.isPending ? "Creating..." : "Create API Key"}
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit API Key Modal */}
      <Modal open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">Edit API Key</ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Update the name of your API key.
            </ModalDescription>
          </ModalHeader>

          <div className="px-[24px] py-[24px] space-y-[20px]">
            <div className="space-y-[8px]">
              <Label htmlFor="edit-key-name" className="text-[13px] text-[#08090A]">Key Name *</Label>
              <Input
                id="edit-key-name"
                placeholder="e.g., Production API Key"
                value={editKeyName}
                onChange={(e) => setEditKeyName(e.target.value)}
                disabled={updateApiKeyMutation.isPending}
                className="h-[36px] px-[13px] rounded-[6px] border-[#EDEEF3] bg-[#FFFFFF] text-[13px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus-visible:ring-0 focus-visible:border-[#654EDA] transition-colors shadow-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !updateApiKeyMutation.isPending) {
                    handleUpdateApiKey()
                  }
                }}
              />
            </div>
          </div>

          <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setIsEditDialogOpen(false)}
              disabled={updateApiKeyMutation.isPending}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateApiKey}
              disabled={updateApiKeyMutation.isPending}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {updateApiKeyMutation.isPending ? "Updating..." : "Update"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete API Key Modal */}
      <Modal open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">Delete API Key</ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Are you sure you want to delete the API key <span className="font-semibold text-[#08090A]">{deletingKey?.name}</span>? This action cannot be undone.
            </ModalDescription>
          </ModalHeader>

          <ModalFooter className="justify-end p-[24px] gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={deleteApiKeyMutation.isPending}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleDeleteApiKey}
              disabled={deleteApiKeyMutation.isPending}
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {deleteApiKeyMutation.isPending ? "Deleting..." : "Delete API Key"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
